import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './CreatorApplicationLinkSocials.scss';

import { openConnectInstagramModal, setUIAuthRedirectUrl } from '../../Actions/UIActions';
import { disconnectSocialAccount, connectInstagram } from '../../Actions/UserActions';

import { openInstagramAuthModal, openYoutubeAuthModal, openTikTokAuthModal } from '../../Helpers/social_helpers';
import {
  getCreatorApplicationConnectedInstagram,
  getCreatorApplicationConnectedYoutube,
  getCreatorApplicationConnectedTiktok
} from '../../Helpers/waitlist_helpers';

import ScrollToTop from '../General/ScrollToTop';
import Modal from '../General/Modal';
import FacebookSDK from '../General/FacebookSDK';
import { ReactComponent as InstagramIcon } from '../../static/images/social_icons/instagram.svg';
import { ReactComponent as TiktokIcon } from '../../static/images/social_icons/tiktok.svg';
import { ReactComponent as YoutubeIcon } from '../../static/images/social_icons/youtube.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

const CreatorApplicationLinkSocials = props => {
  const connectedInstagram = getCreatorApplicationConnectedInstagram(props.application);
  const connectedYoutube = getCreatorApplicationConnectedYoutube(props.application);
  const connectedTiktok = getCreatorApplicationConnectedTiktok(props.application);

  const [isAddingSocialAccount, setIsAddingSocialAccount] = useState(null);
  const [instagramModalVisible, setInstagramModalVisible] = useState(false);
  const close = () => setInstagramModalVisible(false);

  const connectYoutube = () => {
    props.setUIAuthRedirectUrl('/apply/creator');
    if (connectedYoutube.id) return window.ALERT.warn('You have already connected your Youtube account.');
    else if (isAddingSocialAccount) return window.ALERT.error('Please wait for the current connection to finish.');
    else openYoutubeAuthModal();
  };
  const connectTiktok = () => {
    props.setUIAuthRedirectUrl('/apply/creator');
    if (connectedTiktok.id) return window.ALERT.warn('You have already connected your Tiktok account.');
    else if (isAddingSocialAccount) return window.ALERT.error('Please wait for the current connection to finish.');
    else openTikTokAuthModal();
  };
  const connectInstagram = () => {
    props.setUIAuthRedirectUrl('/apply/creator');
    if (connectedInstagram.id) return window.ALERT.warn('You have already connected your Instagram account.');
    else if (isAddingSocialAccount) return window.ALERT.error('Please wait for the current connection to finish.');
    else {
      openInstagramAuthModal(
        async authResponse => {
          setIsAddingSocialAccount(true);
          await props.connectInstagram(authResponse);
          setIsAddingSocialAccount(false);
        },
        () => props.openConnectInstagramModal()
      );
    }
  };

  return (
    <div className='creator-application-link-socials-outer'>
      <ScrollToTop />
      <FacebookSDK />
      <ConnectInstagramWarningModal connectInstagram={connectInstagram} close={close} instagramModalVisible={instagramModalVisible} />
      <div className='creator-application-link-socials-inner'>
        <div className='creator-application-progress-container'>
          <div className='creator-application-progress-container-circle complete'>
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div className='creator-application-progress-container-circle complete'>
            <FontAwesomeIcon icon={faCheck} />
          </div>
        </div>

        <h1>Thank you for your application.</h1>
        <h2>
          We will review it and get back to you within a week. Until then, increase your chances of being accepted by connecting one social account.
        </h2>

        <div className='creator-application-link-socials-button-container'>
          <div className='creator-application-link-socials-button-disclaimer'>Connect your Instagram via Facebook</div>

          <div className='creator-application-link-socials-buttons'>
            <button
              className='creator-application-link-socials-button primary'
              onClick={() => setInstagramModalVisible(true)}
              disabled={!!connectedInstagram.id}
            >
              <InstagramIcon className='creator-application-link-socials-button-icon' /> Connect{connectedInstagram.id ? 'ed' : ''}
            </button>
          </div>

          <div className='creator-application-link-socials-button-disclaimer'>and/or</div>

          <div className='creator-application-link-socials-buttons'>
            <button className={cn('creator-application-link-socials-button primary')} onClick={connectYoutube} disabled={!!connectedYoutube.id}>
              <YoutubeIcon className='creator-application-link-socials-button-icon' /> Connect{connectedYoutube.id ? 'ed' : ''}
            </button>

            <button className='creator-application-link-socials-button primary' onClick={connectTiktok} disabled={!!connectedTiktok.id}>
              <TiktokIcon className='creator-application-link-socials-button-icon' /> Connect{connectedTiktok.id ? 'ed' : ''}
            </button>
          </div>
        </div>

        <div className='creator-application-link-socials-form'>
          <a href='https://shopmy.us/home/creators' className='creator-application-progress-link'>
            {!!connectedInstagram.id || !!connectedYoutube.id || !!connectedTiktok.id ? 'Continue' : "I'll do this later"}
          </a>
        </div>
      </div>
    </div>
  );
};

const ConnectInstagramWarningModal = props => {
  const { connectInstagram, instagramModalVisible } = props;
  const [connectedToFacebook, setConnectedToFacebook] = useState(false);
  const [instagramAccountIsBusinessOrCreator, setInstagramAccountIsBusinessOrCreator] = useState(false);

  return (
    <Modal className='connect-instagram-warning-modal-outer' close={props.close} visible={instagramModalVisible} smallForm showClose>
      <div className='connect-instagram-warning-modal-inner'>
        <div className='connect-instagram-warning-modal-header'>Connect Your Instagram</div>
        <div className='connect-instagram-warning-modal-subheader'>
          Before you proceed, we want to make sure you can connect, due to Instagram's requirements for account connection.
        </div>

        <div className='connect-instagram-warning-modal-body'>
          <div className='connect-instagram-warning-modal-body-checkbox-container' onClick={() => setConnectedToFacebook(!connectedToFacebook)}>
            <input type='checkbox' id='connected-to-facebook' checked={connectedToFacebook} />
            <label htmlFor='connected-to-facebook'>My Instagram account is linked to a facebook page.</label>
          </div>

          <div
            className='connect-instagram-warning-modal-body-checkbox-container'
            onClick={() => setInstagramAccountIsBusinessOrCreator(!instagramAccountIsBusinessOrCreator)}
          >
            <input type='checkbox' id='business-or-creator-account' checked={instagramAccountIsBusinessOrCreator} />
            <label htmlFor='business-or-creator-account'>I have a business or creator instagram account</label>
          </div>
        </div>

        <button
          className='connect-instagram-warning-modal-button'
          onClick={connectInstagram}
          disabled={!connectedToFacebook || !instagramAccountIsBusinessOrCreator}
        >
          Connect Instagram
        </button>

        <div className='connect-instagram-warning-modal-disclaimer'>
          See more on how to connect your Instagram in our{' '}
          <a href='https://guide.shopmy.us/connecting-socials/3ViMaZXyQ3tB63YPsdRTPC' target='_blank' rel='noopener noreferrer'>
            Creator Guide.
          </a>
        </div>
      </div>
    </Modal>
  );
};

CreatorApplicationLinkSocials.propTypes = {
  application: PropTypes.object.isRequired,
  updateCreatorApplication: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  application: state.application
});

export default connect(mapStateToProps, {
  openConnectInstagramModal,
  disconnectSocialAccount,
  connectInstagram,
  setUIAuthRedirectUrl
})(CreatorApplicationLinkSocials);
